import { useState, useRef, useEffect } from 'react';
import { LogOut, User as UserIcon, LogIn, ChevronDown, KeyRound, Shield, Sun, Moon } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { useThemeStore } from '@/store/themeStore';
import { AdminInviteModal } from '@/components/admin/AdminInviteModal';

interface UserNavProps {
  onOpenAuth: () => void;
}

export function UserNav({ onOpenAuth }: UserNavProps) {
  const { user, loading, isAdmin, logout } = useAuth();
  const { resolvedTheme, toggleTheme } = useThemeStore();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isAdminModalOpen, setIsAdminModalOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  if (loading) {
    return (
      <div className="w-24 h-8 bg-cinema-850 rounded-lg animate-pulse border border-cinema-750" />
    );
  }

  if (!user) {
    return (
      <button
        onClick={onOpenAuth}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cinema-850 hover:bg-cinema-800 border border-cinema-700/80 text-xs font-semibold text-cinema-200 hover:text-cinema-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp"
        title="Sign in with your host account to create rooms"
      >
        <LogIn className="w-3.5 h-3.5 text-lamp" />
        <span>Host Sign In</span>
      </button>
    );
  }

  const initial = (user.displayName || user.email || 'H').charAt(0).toUpperCase();

  return (
    <>
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="flex items-center gap-2 p-1.5 pl-2.5 rounded-lg bg-cinema-850 hover:bg-cinema-800 border border-cinema-700/80 text-xs text-cinema-200 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-lamp"
        >
          <span className="w-5 h-5 rounded-full bg-lamp/20 text-lamp border border-lamp/30 flex items-center justify-center font-bold text-[10px]">
            {initial}
          </span>
          <span className="max-w-[120px] truncate text-cinema-100 font-medium">
            {user.displayName || user.email?.split('@')[0]}
          </span>
          {isAdmin && (
            <span className="text-[9px] font-mono px-1.5 py-0.2 rounded bg-lamp/20 text-lamp border border-lamp/30 font-semibold uppercase">
              Admin
            </span>
          )}
          <ChevronDown className="w-3.5 h-3.5 text-cinema-400" />
        </button>

        {isDropdownOpen && (
          <div className="absolute right-0 mt-2 w-52 bg-cinema-900 border border-cinema-700 rounded-lg shadow-xl py-1.5 z-50 animate-fade-in text-xs">
            <div className="px-3 py-2 hairline-b text-cinema-400">
              <div className="flex items-center justify-between">
                <p className="text-[10px] uppercase font-mono tracking-wider text-cinema-500">
                  {isAdmin ? 'Administrator' : 'Host Account'}
                </p>
                {isAdmin && <Shield className="w-3 h-3 text-lamp" />}
              </div>
              <p className="text-cinema-100 font-medium truncate mt-0.5">
                {user.email}
              </p>
            </div>

            {isAdmin && (
              <button
                onClick={() => {
                  setIsDropdownOpen(false);
                  setIsAdminModalOpen(true);
                }}
                className="w-full px-3 py-2.5 text-left text-cinema-200 hover:text-cinema-100 hover:bg-cinema-800 transition-colors flex items-center gap-2 hairline-b border-cinema-800 font-medium"
              >
                <KeyRound className="w-3.5 h-3.5 text-lamp" />
                <span>Manage Invitations</span>
              </button>
            )}

            <button
              onClick={() => {
                toggleTheme();
              }}
              className="w-full px-3 py-2 text-left text-cinema-300 hover:text-cinema-100 hover:bg-cinema-800/60 transition-colors flex items-center justify-between hairline-b border-cinema-800"
            >
              <div className="flex items-center gap-2">
                {resolvedTheme === 'dark' ? <Moon className="w-3.5 h-3.5" /> : <Sun className="w-3.5 h-3.5" />}
                <span>Theme</span>
              </div>
              <span className="text-[10px] uppercase font-mono tracking-wider text-lamp font-semibold">
                {resolvedTheme}
              </span>
            </button>

            <button
              onClick={() => {
                setIsDropdownOpen(false);
                logout();
              }}
              className="w-full px-3 py-2 text-left text-cinema-300 hover:text-red-400 hover:bg-red-500/10 transition-colors flex items-center gap-2"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span>Sign Out</span>
            </button>
          </div>
        )}
      </div>

      {isAdmin && (
        <AdminInviteModal
          isOpen={isAdminModalOpen}
          onClose={() => setIsAdminModalOpen(false)}
        />
      )}
    </>
  );
}

